import {
  CHAT_FILE_UPLOAD_DRAGDROP_IMAGE_CONVERSION_INFO,
  CHAT_FILE_UPLOAD_DRAGDROP_MESSAGE,
  MAX_FILE_SIZE_IN_KB_STRING,
} from "@/constants/ui";
import {
    ERROR_FILE_NOT_PROCESSED,
    ERROR_INVALID_FILE_TYPE,
    ERROR_TITLE_FILE_TOO_LARGE,
    ERROR_TITLE_GENERIC,
    ERROR_UNSUPPORTED_FILE_FORMAT
} from "@/constants/errors";
import {
  MAX_FILE_SIZE,
  WARN_FILE_SIZE,
  convertToWebp,
  isValidFileType,
} from "@/utils/file";
import { useRef, useState } from "react";

import { MoonLoader } from "react-spinners";
import { UploadCloud } from "lucide-react";
import clsx from "clsx";
import { useToast } from "@/components/ui/use-toast";

type DragDropZoneProps = {
  setFile: (file: File | null) => void;
  setFileSizeWarning: (fileSizeWarning: boolean) => void;
  clearFileUploadInputs: () => void;
};

const DragDropZone = ({
  setFile,
  setFileSizeWarning,
  clearFileUploadInputs,
}: DragDropZoneProps) => {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDraggingOver, setIsDraggingOver] = useState<boolean>(false);
  const [isConverting, setIsConverting] = useState<boolean>(false);

  const handleDragOver = (e: React.DragEvent<HTMLInputElement>) => {
    e.preventDefault();
  };

  const handleDragEnter = (e: React.DragEvent<HTMLInputElement>) => {
    e.preventDefault();
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLInputElement>) => {
    e.preventDefault();
    setIsDraggingOver(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLInputElement>) => {
    e.preventDefault();
    setIsDraggingOver(false);

    const files = e.dataTransfer.files;
    handleFileChange(null, files[0]);
  };

  /**
   * Function used to handle file changes
   * @param e Event to get target value from
   * @param droppedFile [optional] File object containing data from uploaded file
   * @returns
   */
  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement> | null,
    droppedFile: File | null = null
  ) => {
    let uploadedFile: File | null = null;

    if (droppedFile) {
      uploadedFile = droppedFile;
    } else if (e && e.target.files && e.target.files.length > 0) {
      uploadedFile = e.target.files[0];
    }

    // Check file format + size
    if (uploadedFile) {
      if (!isValidFileType(uploadedFile)) {
        toast({
          variant: "destructive",
          title: ERROR_UNSUPPORTED_FILE_FORMAT,
          description: ERROR_INVALID_FILE_TYPE,
        });
        clearFileUploadInputs();
        return;
      }

      // convert images to webp to compress them
      if (uploadedFile.type.startsWith("image")) {
        try {
          setIsConverting(true);
          uploadedFile = await convertToWebp(uploadedFile);
        } catch (error) {
          console.error(error);
          toast({
            variant: "destructive",
            title: ERROR_TITLE_GENERIC,
            description: ERROR_FILE_NOT_PROCESSED,
          });
          clearFileUploadInputs();
          setIsConverting(false);
          return;
        }
        setIsConverting(false);
      }

      if (uploadedFile.size > MAX_FILE_SIZE) {
        const fileSizeInKB = (uploadedFile.size / 1024).toFixed(2);

        toast({
          variant: "destructive",
          title: ERROR_TITLE_FILE_TOO_LARGE,
          description: `Your file (${fileSizeInKB} KB) exceeds the ${MAX_FILE_SIZE_IN_KB_STRING} limit. Please reduce the size of your file and try again.`,
        });
        clearFileUploadInputs();
        return;
      } else if (uploadedFile.size > WARN_FILE_SIZE) {
        setFileSizeWarning(true);
      }
    }

    setFile(uploadedFile);
  };

  const triggerFileInput = () => {
    if (fileInputRef && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const dragAndDropClass = clsx(
    "hover:cursor-pointer hover:border-gray-400 transition ease hover:text-gray-500 h-[128px] flex gap-2 flex-col justify-center items-center text-gray-400 rounded-md border-2 border-gray-300 border-dashed",
    {
      "bg-gray-100 border-gray-400": isDraggingOver,
    }
  );

  return (
    <div>
      {isConverting ? (
        <div className={dragAndDropClass}>
          <MoonLoader color="gray" size="30px" />
          <p className="text-sm">
            {CHAT_FILE_UPLOAD_DRAGDROP_IMAGE_CONVERSION_INFO}
          </p>
        </div>
      ) : (
        <div
          className={dragAndDropClass}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={triggerFileInput}
        >
          <UploadCloud size={24} />
          <p className="text-sm">{CHAT_FILE_UPLOAD_DRAGDROP_MESSAGE}</p>
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            onChange={handleFileChange}
          />
        </div>
      )}
    </div>
  );
};

export default DragDropZone;
